import { useRoute } from '#imports'
import { type ComponentPublicInstance, onMounted, onUnmounted } from 'vue'

import { useBuybackOffer } from '@backmarket/nuxt-layer-buyback/app/scopes/offer/composables/useBuybackOffer/useBuybackOffer'
import { useVisitorId } from '@backmarket/nuxt-module-identification/useVisitorId'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import type { EventData } from '@backmarket/nuxt-module-tracking/types'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { capitalize } from '@backmarket/utils/string/capitalize'
import { cleanAlgoliaLabel } from '@backmarket/utils/string/cleanAlgoliaLabel'
import { useIntersectionObserver, useTemplateRefsList } from '@vueuse/core'

import type { Product } from '../models/product'

export type Provider = 'algolia' | 'product page' | 'unknown'

export interface ProductTrackingData extends Partial<EventData> {
  averageRate?: number
  brand: string
  category: string
  color?: string
  currency: string
  dealType: string
  image: string
  isOutOfStock: boolean
  isSwapEnabled: boolean
  list?: string
  listingId: string
  merchantId?: string
  mobilePlanOfferSelected?: string
  model: string
  name: string
  numberTotalReviews?: number
  position?: number
  price: string
  productComponent?: string
  productRecoComponentPosition?: number
  provider?: string
  shippingDelay?: string
  uuid: string
  variant: number
  warrantyDuration?: number
  isFlashSales: boolean
  productGreatPrice: boolean
  productPriceDrop: boolean
  productMarketingTag: string
  personalizationContentCardFamily?: string
}

function cleanEventData(data: Partial<EventData>): EventData {
  const result: EventData = {}

  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined && value !== null) {
      result[key] = value
    }
  }

  return result
}

/** Stable list id from the triggering area (zone), falling back to component / list. */
function getItemListId(data: ProductTrackingData): string {
  return String(data.zone || data.productComponent || data.list || '')
}

export function productToTrackingData(
  product: Product,
  extraTrackingData: ExtraTrackingData,
  dealType: 'normal' = 'normal',
): ProductTrackingData {
  const granularity = product.granularity || extraTrackingData.granularity

  return {
    averageRate: product.reviewRating?.average,
    hasReviews: !!product.reviewRating?.count,
    brand: product.brandClean ?? product.brand,
    category: product.category,
    color: product.specifications?.color?.[0]?.name,
    currency: product.price?.currency,
    dealType,
    id: product.productId,
    isOutOfStock: product.availableStock === 0,
    isSwapEnabled: false,
    image: product.image,
    list: extraTrackingData.list,
    listingId: product.listingId,
    merchantId: product.sellerId,
    mobilePlanOfferSelected: product.mobilePlanOfferId ?? undefined,
    model: product.model,
    name: product.title,
    numberTotalReviews: product.reviewRating?.count,
    position: extraTrackingData.position,
    price: product.price?.amount,
    productComponent: extraTrackingData.productComponent,
    productRecoComponentPosition:
      extraTrackingData.productRecoComponentPosition,
    provider: extraTrackingData.provider,
    shippingDelay: '',
    uuid: product.id,
    variant: product.grade?.value,
    warrantyDuration: product.warrantyDuration,
    isFlashSales: product.deals?.some((d) => d.type === 'flash_sales') ?? false,
    productGreatPrice:
      granularity === 'backbox' &&
      (product.deals?.some((d) => d.type === 'competitive_price') ?? false),
    productPriceDrop:
      granularity === 'backbox' &&
      (product.deals?.some((d) => d.type === 'seller_flash_sales') ?? false),
    productMarketingTag: product.marketingTags?.[0]?.code ?? 'no_tag',
    source: extraTrackingData.source,
    zone: extraTrackingData.zone,
    recoTab: extraTrackingData.recoTab,
    carouselPosition: extraTrackingData.carouselPosition,
    personalizationContentCardFamily:
      extraTrackingData.personalizationContentCardFamily,
  }
}

function usePageReference() {
  const { name, params, fullPath } = useRoute()
  const pageNameParam = params.pageName
  const pageName = Array.isArray(pageNameParam)
    ? pageNameParam.join(',')
    : pageNameParam

  // Convert name to string to avoid type errors
  let routeName = ''
  if (typeof name === 'string') {
    routeName = name
  } else if (typeof name === 'symbol') {
    routeName = String(name)
  }

  return pageName ?? routeName ?? fullPath
}

export function useListTrackingData({
  blockPosition,
}: {
  blockPosition?: number
}) {
  const pageReference = usePageReference()

  return `${pageReference} ${blockPosition}`
}

export function useImpressionTracking(ms = 1000) {
  const trackingData = new Map<Element, ProductTrackingData>()
  const bucket: Set<ProductTrackingData> = new Set()
  const firedLists = new Set<string>()
  const targets = useTemplateRefsList<HTMLElement>()
  let impressionTrackingInterval: ReturnType<typeof setInterval> | null = null

  const { countryCode, defaultCurrency } = useMarketplace().market

  const { stop } = useIntersectionObserver(
    targets,
    (entries) => {
      for (const entry of entries) {
        const trackingDataItem = trackingData.get(entry.target)
        if (entry.isIntersecting && trackingDataItem) {
          bucket.add(trackingDataItem)
        }
      }
    },
    { threshold: 0.8 },
  )

  /**
   * Use is in the template as follow
   * :ref="setupViewportTracking(data)"
   *
   * @param data a serializable JSON object
   */
  function setupViewportTracking(data: ProductTrackingData) {
    return function setTrackingRef(
      el: Element | ComponentPublicInstance | null,
    ) {
      if (el && el instanceof HTMLElement) {
        trackingData.set(el, data)
        targets.value.push(el)
      }

      return el
    }
  }

  function setupViewportTrackingProduct(
    data: Product,
    extra: ExtraTrackingData,
  ) {
    return setupViewportTracking(productToTrackingData(data, extra))
  }

  const {
    trackProductImpressionBatched,
    trackProductImpression,
    trackViewItemList,
  } = useTracking()

  onMounted(() => {
    impressionTrackingInterval = setInterval(() => {
      if (bucket.size === 0) return

      const visibleItems = Array.from(bucket)

      if (visibleItems.length === 1) {
        trackProductImpression(cleanEventData(visibleItems[0]))
      } else {
        trackProductImpressionBatched(
          visibleItems.map((eventData) => cleanEventData(eventData)),
        )
      }

      const itemsByList = new Map<string, ProductTrackingData[]>()
      for (const item of visibleItems) {
        const list = item.list ?? ''
        const group = itemsByList.get(list)
        if (group) {
          group.push(item)
        } else {
          itemsByList.set(list, [item])
        }
      }

      for (const [list, items] of itemsByList) {
        // BB-23: view_item_list once per list for this page/component lifetime
        if (firedLists.has(list)) continue
        firedLists.add(list)

        const listId = items[0] ? getItemListId(items[0]) : list

        trackViewItemList({
          ecommerce: {
            currency: defaultCurrency,
            item_list_id: listId,
            item_list_name: listId,
            items: items.map((data) => ({
              ...toGa4ProductItem(data, countryCode),
              index: toGa4Index(data.position),
              item_list_id: list,
              item_list_name: list,
            })),
          },
        })
      }

      bucket.clear()
    }, ms)
  })

  onUnmounted(() => {
    if (impressionTrackingInterval) {
      clearInterval(impressionTrackingInterval)
    }
    stop()
    trackingData.clear()
    firedLists.clear()
  })

  return { setupViewportTracking, setupViewportTrackingProduct }
}

export type ExtraTrackingData = Partial<{
  provider: Provider
  list: string
  zone?: string
  recoTab?: string
  carouselPosition?: number
  position?: number
  source?: string
  queryID?: string
  index?: string
  granularity?: 'parent' | 'variant' | 'backbox'
  productComponent: string
  productRecoComponentPosition: number
  personalizationContentCardFamily?: string
}>
export function useClickTracking() {
  const {
    trackProductClick: genericTrackProductClick,
    trackSearchAnalytics,
    trackSelectItem,
  } = useTracking()
  const { countryCode } = useMarketplace().market
  const visitorId = useVisitorId()

  function trackProductClick(
    product: Product,
    extraTrackingData: ExtraTrackingData,
  ) {
    const trackingData = productToTrackingData(product, extraTrackingData)

    genericTrackProductClick(cleanEventData(trackingData))

    const list = trackingData.list ?? ''
    const listId = getItemListId(trackingData)
    const ga4Item = {
      ...toGa4ProductItem(trackingData, countryCode),
      index: toGa4Index(trackingData.position),
      item_list_id: listId,
      item_list_name: list,
    }

    trackSelectItem({
      ecommerce: {
        currency: trackingData.currency,
        item_list_id: listId,
        item_list_name: list,
        items: [ga4Item],
      },
    })

    const { queryID, index, position } = extraTrackingData
    if (queryID && index && position && product.objectId) {
      trackSearchAnalytics({
        type: 'click',
        objectId: product.objectId,
        searchQueryID: queryID,
        tokenId: visitorId,
        position,
        index,
      })
    }
  }

  return { trackProductClick }
}

function toGa4Index(position?: number) {
  return Math.max((position ?? 1) - 1, 0)
}

function toGa4ProductItem(data: ProductTrackingData, locationId: string) {
  const price =
    typeof data.price === 'string' ? parseFloat(data.price) : data.price

  return {
    item_id:
      typeof data.id === 'string' || typeof data.id === 'number'
        ? String(data.id)
        : '',
    item_uuid: typeof data.uuid === 'string' ? data.uuid : '',
    item_name: data.name,
    item_brand: capitalize(cleanAlgoliaLabel(data.brand)),
    item_category: data.category,
    price,
    quantity: 1,
    affiliation: data.merchantId ?? '',
    index: data.position,
    item_list_id: data.list,
    item_list_name: data.list,
    item_variant: data.variant,
    item_warranty_duration: data.warrantyDuration,
    location_id: locationId,
    google_business_vertical: 'retail',
  }
}

export function useAddToCartTracking() {
  const { countryCode } = useMarketplace().market
  const { hasBuybackOffer } = useBuybackOffer()

  return {
    trackAddToCart: (trackingData: ProductTrackingData, zone: string) => {
      const { trackAddToCart: _trackAddToCart, trackAddToCartGa4 } =
        useTracking()
      _trackAddToCart({ ...trackingData, zone })
      const ga4Item = {
        ...toGa4ProductItem(trackingData, countryCode),
        index: toGa4Index(trackingData.position),
        item_list_id: zone,
        item_list_name: zone,
      }
      trackAddToCartGa4({
        zone,
        currency: trackingData.currency,
        hasTradeIn: hasBuybackOffer.value,
        value: (ga4Item.price ?? 0) * ga4Item.quantity,
        items: [ga4Item],
      })
    },

    trackAddToCartBatch: (
      products: Product[],
      extraTrackingData: ExtraTrackingData,
      list: string,
      zone?: string,
    ) => {
      const { trackAddToCartBatch, trackAddToCartGa4 } = useTracking()
      const trackingProducts = products.map((product, index) =>
        productToTrackingData(product, {
          ...extraTrackingData,
          // Bundle ATC is the exception: 1-based index among bundle items, not the PDP product.
          position: index + 1,
        }),
      )
      trackAddToCartBatch({ products: trackingProducts, list, zone })
      const ga4Items = trackingProducts.map((p) => ({
        ...toGa4ProductItem(p, countryCode),
        item_list_id: list,
        item_list_name: list,
      }))
      const currency = trackingProducts[0]?.currency ?? ''
      const value = ga4Items.reduce(
        (sum, item) => sum + (item.price ?? 0) * item.quantity,
        0,
      )
      trackAddToCartGa4({
        zone: list,
        currency,
        hasTradeIn: hasBuybackOffer.value,
        value,
        items: ga4Items,
      })
    },
  }
}
